/* eslint-disable no-secrets/no-secrets */
import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { StoreAndLanguage } from '@core/domain/state/core.state';
import { StaticResourceService } from '@core/services/static-resource.service';
import { TranslationService } from '@core/services/translation.service';
import { getSelectedPaymentMethod } from '@shared/payment/store';
import { TermsConditions } from '@shared/terms-conditions/models/terms-conditions.model';
import { SetDscAcceptanceRequired } from '@shared/terms-conditions/store/terms-conditions.actions';

type TermsConditionsMethod = (...args: any[]) => Observable<TermsConditions[]>;

const KOREAN_LANGUAGE_ID = '-9';

@Injectable()
export class InputTermsConditionsService extends MarketConfigurable<Record<string, any>> {
    private languageId: string;

    protected readonly config: Record<string, Record<string, TermsConditionsMethod>> = {
        KR: {
            getPurchaseConditions: this.getPurchaseConditionsKR.bind(this),
            getPurchaseConditionsRGPD: (_userType: string) => of([] as TermsConditions[]),
        },
        TR: {
            getPurchaseConditions: this.getPurchaseConditionsTR.bind(this),
            getPurchaseConditionsRGPD: (_userType: string) => of([] as TermsConditions[]),
        },
        default: {
            getAddressConditionsRGPD: this.getAddressConditionsRGPDCommon.bind(this),
            getContactConditions: this.getContactConditionsCommon.bind(this),
            getContactConditionsRGPD: this.getContactConditionsRGPDCommon.bind(this),
            getContactConditionsOptIn: this.getContactConditionsOptInCommon.bind(this),
            getDataTransferConditions: this.getDataTransferConditionsCommon.bind(this),
            getDataTraspasOut: this.getDataTraspasOutCommon.bind(this),
            getFeelRegisterConditions: this.getFeelRegisterConditionsCommon.bind(this),
            getFeelRegisterConditionsRGPD: this.getFeelRegisterConditionsRGPDCommon.bind(this),
            getFeelConditionsRGPD: this.getFeelConditionsRGPDCommon.bind(this),
            getGiftCardConditions: this.getGiftCardConditionsCommon.bind(this),
            getGiftCardConditionsRGPD: this.getGiftCardConditionsRGPDCommon.bind(this),
            getGiftCardConditionsReturnRGPD: this.getGiftCardConditionsReturnRGPDCommon.bind(this),
            getNewsletterConditions: this.getNewsletterConditionsCommon.bind(this),
            getNewsletterConditionsRGPD: this.getNewsletterConditionsRGPDCommon.bind(this),
            getNewsletterConditionsRegisterCommon: this.getNewsletterConditionsRegisterCommon.bind(this),
            getNewsletterConditionsRegisterCommonRGPD: this.getNewsletterConditionsRegisterCommonRGPD.bind(this),
            getNewsletterConditionsKorea: this.getNewsletterConditionsKoreaCommon.bind(this),
            getPersonalInformationConditions: this.getPersonalInformationConditionsCommon.bind(this),
            getPersonalInformationNewsletterConditions: this.getPersonalInformationNewsletterConditionsCommon.bind(this),
            getPersonalInformationOverseasConditions: this.getPersonalInformationOverseasConditionsCommon.bind(this),
            getProductStatusConditions: this.getProductStatusConditionsCommon.bind(this),
            getProductStatusConditionsRGPD: this.getProductStatusConditionsRGPDCommon.bind(this),
            getProductStatusConditionsOptIn: this.getProductStatusConditionsOptInCommon.bind(this),
            getPurchaseConditions: this.getPurchaseConditionsCommon.bind(this),
            getPurchaseConditionsRGPD: this.getPurchaseConditionsRGPDCommon.bind(this),
            getPurchaseConditionsRGPDGuest: this.getPurchaseConditionsRGPDGuest.bind(this),
            getRedeemFeelGiftConditions: this.getRedeemFeelGiftConditionsCommon.bind(this),
            getRegisterAddressSocialContactConditions: this.getRegisterAddressSocialContactConditionsCommon.bind(this),
            // eslint-disable-next-line max-len
            getRegisterAddressSocialContactConditionsOptIn: this.getRegisterAddressSocialContactConditionsOptInCommon.bind(this),
            getRegisterConditions: this.getRegisterConditionsCommon.bind(this),
            getRegisterConditionsRGPD: this.getRegisterConditionsRGPDCommon.bind(this),
            getRegisterConditionsUnder14: this.getRegisterConditionsUnder14Common.bind(this),
            getRegisterConditionsImplicit: this.getRegisterConditionsImplicitCommon.bind(this),
            getSensitiveDataConditions: this.getSensitiveDataConditionsCommon.bind(this),
            getSocialConditionsRGPD: this.getSocialConditionsRGPDCommon.bind(this),
            getStorageOfPaymentConditions: this.getStorageOfPaymentConditionsCommon.bind(this),
            getStorageOfPaymentConditionsRGPD: this.getStorageOfPaymentConditionsRGPDCommon.bind(this),
        },
    };

    // Solo utilizamos este observable para escuchar cambios en traducciones
    public readonly translations$ = this.translationService.getTranslation(this.translateService.currentLang, 'dev').pipe(map(() => null));

    constructor(
        protected store: Store,
        protected translateService: TranslateService,
        protected translationService: TranslationService,
        protected staticResourceSvc: StaticResourceService
    ) {
        super();
    }

    public getTermCondition(getCondition: string, countryAndLanguage: StoreAndLanguage): Observable<TermsConditions[]> {
        const { countryCode } = countryAndLanguage;
        this.languageId = countryAndLanguage.languageId;
        const config = this.getConfigByMarketCode(countryCode);
        if (config[getCondition]) {
            return config[getCondition]();
        }
        return this.getConfigByMarketCode('default')[getCondition]();
    }

    private getPurchaseConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.checkout.purchaseConditions',
            'dev.checkout.payment.and',
            'dev.checkout.payment.policies',
            'legacy.ItxOrderPaymentPage.accept',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    type: 'purchaseConditionsCommon',
                    label: 'PCC',
                    messages: [
                        {
                            text: translations['legacy.ItxOrderPaymentPage.accept'],
                        },
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.checkout.payment.and'],
                        },
                        {
                            text: translations['dev.checkout.payment.policies'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [mainTerms];
            }),
            tap(() => { this.store.dispatch(SetDscAcceptanceRequired({ acceptanceRequired: false })); })
        );
    }

    private getRegisterConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.userRegistration.msgPolicy2',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'layer_6A',
                    type: 'registerConditionsCommon',
                    label: 'RCC',
                    messages: [
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy2'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };
                return [mainTerms];
            })
        );
    }

    private getRegisterConditionsImplicitCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.register.descriptionPrivacyPolicyPart1',
            'dev.register.descriptionPrivacyPolicyPart2',
            'dev.register.descriptionPrivacyPolicyPart3',
            'dev.register.descriptionPrivacyPolicyPart4',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'layer_2',
                    type: 'registerConditionsImplicitCommon',
                    label: 'RCCNC',
                    messages: [
                        {
                            text: translations['dev.register.descriptionPrivacyPolicyPart1'],
                        },
                        {
                            text: translations['dev.register.descriptionPrivacyPolicyPart2'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.register.descriptionPrivacyPolicyPart3'],
                        },
                        {
                            text: translations['dev.register.descriptionPrivacyPolicyPart4'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };
                return [mainTerms];
            })
        );
    }

    private getPurchaseConditionsKR(): Observable<TermsConditions[]> {
        if (this.languageId !== KOREAN_LANGUAGE_ID) {
            return this.translateService.get([
                'dev.checkout.purchaseConditions',
                'dev.checkout.payment.and',
                'dev.checkout.payment.policies',
                'legacy.ItxOrderPaymentPage.accept',
                'dev.terminosNicePay1',
                'dev.terminosNicePay2',
                'dev.terminosNicePay3',
            ]).pipe(
                concatLatestFrom(()=>this.store.select(getSelectedPaymentMethod)),
                map(([translations, selectedPaymentMethod]) => {
                    const mainTerms: TermsConditions = {
                        type: 'purchaseConditionsKR',
                        label: 'PCKR',
                        messages: [
                            {
                                text: translations['legacy.ItxOrderPaymentPage.accept'],
                            },
                            {
                                text: translations['dev.checkout.purchaseConditions'],
                                clickText: 'openPurchaseCondition',
                            },
                            {
                                text: translations['dev.checkout.payment.and'],
                            },
                            {
                                text: translations['dev.checkout.payment.policies'],
                                clickText: 'openPrivacyPolicy',
                            },
                        ],
                    };
                    const paymentSpecialTerms: TermsConditions = {
                        type: 'purchasePaymentConditionsKR',
                        label: 'PPCKR',
                        messages: [
                            {
                                text: translations['dev.terminosNicePay1'],
                            },
                            {
                                text: translations['dev.terminosNicePay2'],
                                clickText: 'openKoreaPurchaseCondition',
                            },
                            {
                                text: translations['dev.terminosNicePay3'],
                            },
                        ],
                    };
                    if (selectedPaymentMethod.name === 'Kakaopay' || selectedPaymentMethod.name === 'NaverPay') {
                        return [mainTerms, paymentSpecialTerms];
                    }
                    return [mainTerms];
                }),
                tap(() => { this.store.dispatch(SetDscAcceptanceRequired({ acceptanceRequired: false })); })
            );
        }

        return this.translateService.get([
            'legacy.ItxOrderPaymentPage.accept',
            'dev.checkout.purchaseConditions',
            'dev.checkout.payment.and',
            'dev.checkout.payment.policies',
            'dev.terminosNicePay1',
            'dev.terminosNicePay2',
            'dev.terminosNicePay3',
        ]).pipe(
            concatLatestFrom(() => this.store.select(getSelectedPaymentMethod)),
            map(([translations, selectedPaymentMethod]) => {
                const mainTerms: TermsConditions = {
                    type: 'purchaseConditionsKR',
                    label: 'PCKR',
                    messages: [
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.checkout.payment.and'],
                        },
                        {
                            text: translations['dev.checkout.payment.policies'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['legacy.ItxOrderPaymentPage.accept'],
                        },
                    ],
                };
                const paymentSpecialTerms: TermsConditions = {
                    type: 'purchasePaymentConditionsKR',
                    label: 'PPCKR',
                    messages: [
                        {
                            text: translations['dev.terminosNicePay1'],
                        },
                        {
                            text: translations['dev.terminosNicePay2'],
                            clickText: 'openKoreaPurchaseCondition',
                        },
                        {
                            text: translations['dev.terminosNicePay3'],
                        },
                    ],
                };
                if (selectedPaymentMethod.name === 'Kakaopay' || selectedPaymentMethod.name === 'NaverPay') {
                    return [mainTerms, paymentSpecialTerms];
                }
                return [mainTerms];
            }),
            tap(() => { this.store.dispatch(SetDscAcceptanceRequired({ acceptanceRequired: false })); })
        );
    }

    private getPurchaseConditionsTR(): Observable<TermsConditions[]> {
        // Obtenemos las traducciones
        return this.translateService.get([
            'dev.checkout.payment.and',
            'legacy.ItxOrderPaymentPage.cancellationPolicies',
            'legacy.ItxOrderPaymentPage.accept',
            'dev.checkout.payment.policies',
            'legacy.ItxOrderPaymentPage.conditions',
            'legacy.ItxOrderPaymentPage.distantSalesContract',
        ]).pipe(
            map((translations: any) => {
                // Términos para privacidad y cancelación
                const mainTerms: TermsConditions = {
                    externalScrollContent: 'CancellationPolicy',
                    externalScrollContentType: 'mspot',

                    type: 'purchaseConditionsTRMainTerms',
                    label: 'PCTRMT',
                    messages: [
                        {
                            text: translations['legacy.ItxOrderPaymentPage.accept'],
                        },
                        {
                            text: translations['dev.checkout.payment.policies'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.checkout.payment.and'],
                        },
                        {
                            text: translations['legacy.ItxOrderPaymentPage.cancellationPolicies'],
                            clickText: 'openCancellationPolicies',
                        },
                    ],
                };

                const checkoutTerms: TermsConditions = {
                    externalScrollContent: 'TermsAndConditions',
                    externalScrollContentType: 'mspot',

                    type: 'purchaseConditionsTRCheckoutTerms',
                    label: 'PCTRCT',
                    messages: [
                        {
                            text: `${translations['legacy.ItxOrderPaymentPage.accept']} ${translations['legacy.ItxOrderPaymentPage.conditions']}`,
                        },
                    ],
                };

                const lineTerms: TermsConditions = {
                    externalScrollContent: 'DistantSaleContractContentWeb',
                    externalScrollContentType: 'mspot',
                    type: 'purchaseConditionsTRLineTerms',
                    label: 'PCTRLT',
                    // extrernalScrollContentCompile: true,
                    messages: [
                        {
                            text: `${translations['legacy.ItxOrderPaymentPage.accept']} ${translations['legacy.ItxOrderPaymentPage.distantSalesContract']}`,
                        },
                    ],
                };

                return [mainTerms, checkoutTerms, lineTerms];
            }),
            // Desactivado por ahora mientras el back no esté preparado para gestionar la nueva petición de aceptación de DSC
            tap(() => { this.store.dispatch(SetDscAcceptanceRequired({ acceptanceRequired: false })); })
        );
    }

    private getRegisterConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.register1',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'layer_2',
                    type: 'registerConditionsRGPDCommon',
                    label: 'RCRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.register1'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [mainTerms];
            })
        );
    }

    private getRegisterConditionsUnder14Common() {
        return this.translateService.get([
            'dev.register.under14Condition',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'NO_LAYER',
                    type: 'registerConditionsUnder14Common',
                    label: 'RCU14C',
                    messages: [
                        {
                            text: translations['dev.register.under14Condition'],
                        },
                    ],
                };

                return [mainTerms];
            })
        );
    }

    private getPurchaseConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.purchase1',
            'dev.checkout.purchaseConditions',
            'dev.rgpd.purchase3',
            'dev.general.privacyPolicy',
            'dev.rgpd.purchase5',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'NO_LAYER',

                    type: 'purchaseConditionsRGPDCommon',
                    label: 'PCRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.purchase1'],
                        },
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.rgpd.purchase3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'windowOpenPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.purchase5'],
                        },
                    ],
                };

                return [mainTerms];
            })
        );
    }

    private getPurchaseConditionsRGPDGuest(_userType: string): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.purchase1',
            'dev.checkout.purchaseConditions',
            'dev.rgpd.purchase3',
            'dev.general.privacyPolicy',
            'dev.rgpd.purchase5',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'layer_6A',
                    twoTerms: true,
                    tooltipMode: true,

                    type: 'purchaseConditionsRGPDGuest',
                    label: 'PCRGPDCG',
                    messages: [
                        {
                            text: translations['dev.rgpd.purchase1'],
                        },
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.rgpd.purchase3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.purchase5'],
                        },
                    ],
                };

                return [mainTerms];
            })
        );
    }

    private getAddressConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'legacy.rgpd.PurchaseGuest_1',
            'dev.general.privacyPolicy',
            'legacy.rgpd.PurchaseGuest_3',
        ]).pipe(
            map((translations) => {
                const mainTerms: TermsConditions = {
                    layer: 'NO_LAYER',
                    type: 'addressConditionsRGPDCommon',
                    label: 'ACRGPDC',
                    messages: [
                        {
                            text: translations['legacy.rgpd.PurchaseGuest_1'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['legacy.rgpd.PurchaseGuest_3'],
                        },
                    ],
                };

                return [mainTerms];
            })
        );
    }

    private getContactConditionsRGPDCommon(): Observable<TermsConditions[]> {
        // Obtenemos las traducciones
        return this.translateService.get([
            'dev.rgpd.register1',
            'dev.general.privacyPolicy',
            'dev.rgpd.register3',
        ]).pipe(
            map((translations: any) => {
                const lineTerms: TermsConditions = {
                    layer: 'layer_11',
                    type: 'contactConditionsRGPDCommon',
                    label: 'CCRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.register1'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.register3'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getFeelRegisterConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'CONT.TITLE.SECTION.FEEL',
            'dev.identity.register.feel.joinmessage',
            'dev.rgpd.registerFeel1',
            'dev.rgpd.registerFeel2',
            'dev.rgpd.registerFeel3',
            'dev.general.privacyPolicy',
            'dev.rgpd.registerFeel5',
        ]).pipe(
            map(translations => {
                const lineTerms: TermsConditions = {
                    layer: 'NO_LAYER',
                    type: 'feelRegisterConditionsRGPDCommon',
                    label: 'FRC_RGPDC',
                    messages: [
                        {
                            text: translations['CONT.TITLE.SECTION.FEEL'],
                        },
                        {
                            text: translations['dev.identity.register.feel.joinmessage'],
                        },
                        {
                            text: translations['dev.rgpd.registerFeel1'],
                        },
                        {
                            text: translations['dev.rgpd.registerFeel2'],
                            clickText: 'windowOpenFeelTermsConditions',
                        },
                        {
                            text: translations['dev.rgpd.registerFeel3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'windowOpenFeelPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.registerFeel5'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getFeelConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.identity.register.feel.joinmessage',
            'dev.rgpd.registerFeel1',
            'dev.rgpd.registerFeel2',
            'dev.rgpd.registerFeel3',
            'dev.general.privacyPolicy',
            'dev.rgpd.registerFeel5',
        ]).pipe(
            map(translations => {
                const lineTerms: TermsConditions = {
                    layer: 'NO_LAYER',
                    type: 'feelConditionsRGPDCommon',
                    label: 'FRC_RGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.registerFeel1'],
                        },
                        {
                            text: translations['dev.rgpd.registerFeel2'],
                            clickText: 'windowOpenFeelTermsConditions',
                        },
                        {
                            text: translations['dev.rgpd.registerFeel3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'windowOpenFeelPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.registerFeel5'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getNewsletterConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.form.newsletter.rgpd.register1',
            'dev.general.privacyPolicy',
            'dev.myAccount.newsletter.msgpolicy3.optIn',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    type: 'newsletterConditionsRGPDCommon',
                    label: 'NC_RGPDC',
                    messages: [
                        {
                            text: translations['dev.form.newsletter.rgpd.register1'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.myAccount.newsletter.msgpolicy3.optIn'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getProductStatusConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.privacyPolicy.iHaveRead',
            'dev.general.privacyPolicy',
            'dev.forms.privacyPolicy.iConsent',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    type: 'productStatusConditionsRGPDCommon',
                    label: 'PSCRGPDC',
                    messages: [
                        {
                            text: translations['dev.forms.privacyPolicy.iHaveRead'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'windowOpenPrivacyPolicy',
                        },
                        {
                            text: translations['dev.forms.privacyPolicy.iConsent'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getGiftCardConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.giftCard1',
            'dev.giftCard.useConditions',
            'dev.rgpd.giftCard3',
            'dev.general.privacyPolicy',
            'dev.rgpd.giftCard5',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    layer: 'layer_7',
                    type: 'giftCardConditionsRGPDCommon',
                    label: 'GCCRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.giftCard1'],
                        },
                        {
                            text: translations['dev.giftCard.useConditions'],
                            clickText: 'openGiftCardConditions',
                        },
                        {
                            text: translations['dev.rgpd.giftCard3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.giftCard5'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getGiftCardConditionsReturnRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.purchase1',
            'dev.checkout.purchaseConditions',
            'dev.rgpd.purchase3',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    layer: 'layer_8',
                    type: 'giftCardConditionsReturnRGPDCommon',
                    label: 'GCCRRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.purchase1'],
                        },
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.rgpd.purchase3'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getStorageOfPaymentConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.storageOfPayment1',
            'dev.general.privacyPolicy',
            'dev.rgpd.storageOfPayment3',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    layer: 'layer_12',

                    type: 'storageOfPaymentConditionsRGPDCommon',
                    label: 'SOPCRGPDC',
                    messages: [
                        {
                            text: translations['dev.rgpd.storageOfPayment1'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.storageOfPayment3'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getSocialConditionsRGPDCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.privacyPolicy.iHaveRead',
            'dev.general.privacyPolicy',
            'dev.forms.privacyPolicy.iConsent',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    layer: 'layer_12',
                    type: 'socialConditionsRGPDCommon',
                    label: 'SCRGPDC',
                    messages: [
                        {
                            text: translations['dev.forms.privacyPolicy.iHaveRead'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.forms.privacyPolicy.iConsent'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getFeelRegisterConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.identity.register.feel.legalDisclaimer',
            'dev.general.useConditions',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                let legalText = translations['dev.identity.register.feel.legalDisclaimer'];
                let splittedLegalText1 = legalText.split('{{ $0 }}');
                let splittedLegalText2 = splittedLegalText1[1].split('{{ $1 }}');
                const lineTerms: TermsConditions = {
                    layer: 'layer_2',
                    type: 'feelRegisterConditionsCommon',
                    label: 'FRC_C',
                    messages: [
                        {
                            text: splittedLegalText1[0].trim(),
                        },
                        {
                            text: translations['dev.general.useConditions'].trim(),
                            clickText: 'windowOpenFeelTermsConditions',
                        },
                        {
                            text: splittedLegalText2[0].trim(),
                        },
                        {
                            text: translations['dev.general.privacyPolicy'].trim(),
                            clickText: 'windowOpenFeelPrivacyPolicy',
                        },
                        {
                            text: splittedLegalText2[1].trim(),
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getProductStatusConditionsOptInCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.privacyPolicy.iHaveRead',
            'dev.general.privacyPolicy',
            'dev.forms.privacyPolicy.iConsent',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'PSCOIC',
                    type: 'productStatusConditionsOptInCommon',
                    messages: [
                        {
                            text: translations['dev.forms.privacyPolicy.iHaveRead'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.forms.privacyPolicy.iConsent'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getProductStatusConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.modal.contact.privacyPolicy',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'PSCC',
                    type: 'productStatusConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.modal.contact.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getContactConditionsOptInCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.userRegistration.msgPolicy2.optIn',
            'dev.forms.userRegistration.msgPolicy3.optIn',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'CCOIC',
                    type: 'contactConditionsOptInCommon',
                    messages: [
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy2.optIn'],
                        },
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy3.optIn'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getContactConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.userRegistration.msgPolicy2',
            'dev.forms.userRegistration.msgPolicy3',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'CCC',
                    type: 'contactConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy2'],
                        },
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy3'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getRegisterAddressSocialContactConditionsOptInCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.userRegistration.msgPolicy2.optIn',
            'dev.forms.userRegistration.msgPolicy3.optIn',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'RCASCOIC',
                    type: 'registerConditionsAddressSocialContactConditionsOptInCommon',
                    messages: [
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy2.optIn'],
                        },
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy3.optIn'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: '*',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getRegisterAddressSocialContactConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.forms.userRegistration.msgPolicy2',
            'dev.forms.userRegistration.msgPolicy3',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'RCASCC',
                    type: 'registerConditionsAddressSocialContactCommon',
                    messages: [
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy2'],
                        },
                        {
                            text: translations['dev.forms.userRegistration.msgPolicy3'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getPersonalInformationConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.modal.mandatoryCollection1KR',
            'dev.modal.mandatoryCollection2KR',
            'dev.modal.mandatoryCollection3KR',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'PICC',
                    type: 'personalInformationConditionsCommon',
                    layer: 'KR_Terms_Policy',
                    messages: [
                        {
                            text: translations['dev.modal.mandatoryCollection1KR'],
                        },
                        {
                            text: translations['dev.modal.mandatoryCollection2KR'],
                            clickText: 'openKoreaPersonalInformation',
                        },
                        {
                            text: translations['dev.modal.mandatoryCollection3KR'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getPersonalInformationNewsletterConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'legacy.ItxContactPopup.terms_email_kr',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'PINC_C',
                    type: 'personalInformationNewsletterConditionsCommon',
                    layer: 'KR_Terms_Email',
                    messages: [
                        {
                            text: translations['legacy.ItxContactPopup.terms_email_kr'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getDataTraspasOutCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.general.dataProtection.traspasout',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'DTO_C',
                    type: 'getDataTraspasOutCommon',
                    messages: [
                        {
                            text: translations['dev.general.dataProtection.traspasout'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getDataTransferConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.general.dataProtection.externalDataTransfer',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'DTC_C',
                    type: 'dataTransferConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.general.dataProtection.externalDataTransfer'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getSensitiveDataConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.general.dataProtection.sensitiveData',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'SDC_C',
                    type: 'sensitiveDataConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.general.dataProtection.sensitiveData'],
                        },
                        {
                            text: '*',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getPersonalInformationOverseasConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.modal.optionalCollection2KR',
            'dev.modal.optionalCollection3KR',
            'dev.modal.optionalCollection1KR',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'PIOCC',
                    type: 'personalInformationOverseasConditionsCommon',
                    layer: 'KR_Terms_Conditions',
                    messages: [
                        {
                            text: translations['dev.modal.optionalCollection2KR'],
                        },
                        {
                            text: translations['dev.modal.optionalCollection3KR'],
                            clickText: 'openKoreaPersonalInformation',
                        },
                        {
                            text: translations['dev.modal.optionalCollection1KR'],
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getStorageOfPaymentConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.checkout.payment.card.saveCard',
            'dev.checkout.purchaseConditions',
            'dev.checkout.payment.and',
            'dev.checkout.payment.policies',
            'dev.rgpd.storageOfPayment3',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'SOPCC0',
                    type: 'storageOfPaymentConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.checkout.payment.card.saveCard'],
                        },
                        {
                            text: translations['dev.checkout.purchaseConditions'],
                            clickText: 'openPurchaseCondition',
                        },
                        {
                            text: translations['dev.checkout.payment.and'],
                        },
                        {
                            text: translations['dev.checkout.payment.policies'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.rgpd.storageOfPayment3'],
                        },
                    ],
                };
                return [lineTerms];
            })
        );
    }

    private getGiftCardConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.giftCard.accept',
            'dev.giftCard.useConditions',
            'dev.checkout.payment.and',
            'dev.checkout.payment.policies',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'GCCC',
                    type: 'giftCardConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.giftCard.accept'],
                        },
                        {
                            text: translations['dev.giftCard.useConditions'],
                            clickText: 'openGiftCardConditions',
                        },
                        {
                            text: translations['dev.checkout.payment.and'],
                        },
                        {
                            text: translations['dev.checkout.payment.policies'],
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }

    private getNewsletterConditionsKoreaCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.modal.contact.termsPolicy',
            'dev.modal.contact.termsConditions',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'NC_KC',
                    type: 'newsletterConditionsKoreaCommon',
                    messages: [
                        {
                            text: translations['dev.modal.contact.termsPolicy'],
                            clickText: 'openKoreaPersonalInformation',
                        },
                        {
                            text: translations['dev.modal.contact.termsConditions'],
                            clickText: 'openKoreaPersonalInformationOverseas',
                        },
                    ],
                };
                return [lineTerms];
            })
        );
    }

    private getNewsletterConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.myAccount.newsletter.msgpolicy1.optIn',
            'dev.general.privacyPolicy',
            'dev.myAccount.newsletter.msgpolicy3.optIn',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'NC_C',
                    type: 'newsletterConditionsCommon',
                    messages: [
                        {
                            text: translations['dev.myAccount.newsletter.msgpolicy1.optIn'],
                        },
                        {
                            text: translations['dev.general.privacyPolicy'],
                            clickText: 'openPrivacyPolicy',
                        },
                        {
                            text: translations['dev.myAccount.newsletter.msgpolicy3.optIn'],
                        },
                    ],
                };
                return [lineTerms];
            })
        );
    }

    private getNewsletterConditionsRegisterCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.form.newsletter.optIn',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'NC_RC',
                    type: 'newsletterConditionsRegisterCommon',
                    messages: [
                        {
                            text: translations['dev.form.newsletter.optIn'],
                        },
                    ],
                };
                return [lineTerms];
            })
        );
    }

    private getNewsletterConditionsRegisterCommonRGPD(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.rgpd.registerNewsletter1',
        ]).pipe(
            map((translations) => {
                const lineTerms: TermsConditions = {
                    label: 'NC_RCRGPD',
                    type: 'newsletterConditionsRegisterCommon',
                    messages: [
                        {
                            text: translations['dev.rgpd.registerNewsletter1'],
                        },
                    ],
                };
                return [lineTerms];
            })
        );
    }

    private getRedeemFeelGiftConditionsCommon(): Observable<TermsConditions[]> {
        return this.translateService.get([
            'dev.myAccount.feel.redeemGift.step2.privacyPolicy',
            'dev.general.privacyPolicy',
        ]).pipe(
            map((translations) => {
                let legalText = translations['dev.myAccount.feel.redeemGift.step2.privacyPolicy'];
                let splittedLegalText1 = legalText.split('{{ $0 }}');
                const lineTerms: TermsConditions = {
                    label: 'RFGCC',
                    type: 'redeemFeelGiftConditionsCommon',
                    messages: [
                        {
                            text: splittedLegalText1[0].trim(),
                        },
                        {
                            text: translations['dev.general.privacyPolicy'].trim(),
                            clickText: 'openPrivacyPolicy',
                        },
                    ],
                };

                return [lineTerms];
            })
        );
    }
}

// function mixinInputTermsConditionsService<B extends Constructor<InputTermsConditionsService>>(Base: B): B {
//     return class extends Base {
//         // constructor(...args: any []) {
//         //     super(...args);
//         // }
//     };
// }

// const PurchaseMixin = mixinInputTermsConditionsService(InputTermsConditionsService);
// // #endregion PURCHASE CONDITIONS

// @Injectable()
// export class InputTermsConditionsService extends PurchaseMixin {
//     constructor(
//         protected store: Store,
//         protected translateService: TranslateService
//     ) {
//         super(store, translateService);
//     }
// }
